import { useEffect, useState } from 'react';

import { METRIC_SEED, coveragePhrases } from '@/config/platform-metrics';
import { fetchPlatformMetrics, type PlatformMetrics } from '@/lib/platformMetrics';

/**
 * Current platform numbers.
 *
 * Returns the compiled seed synchronously, then swaps in the live values when
 * they arrive. Snapshot keys (connector counts) stay on the seed even if the
 * RPC returns a number. There is deliberately no loading state: a number is
 * either the current one or last release's one, and both are real. Rendering
 * a skeleton where a figure belongs would trade a small staleness for a layout
 * shift on every page that prints a count.
 *
 * The fetch is memoized in the module, so mounting this in ten components costs
 * one request.
 */
export function usePlatformMetrics(): PlatformMetrics {
  const [metrics, setMetrics] = useState<PlatformMetrics>(
    () => ({ ...METRIC_SEED }) as PlatformMetrics,
  );

  useEffect(() => {
    let alive = true;
    void fetchPlatformMetrics().then((live) => {
      if (alive) setMetrics(live);
    });
    return () => {
      alive = false;
    };
  }, []);

  return metrics;
}

/** The phrasings, built from whatever numbers are current. */
export function useCoveragePhrases() {
  return coveragePhrases(usePlatformMetrics());
}
